import { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Settings, ChevronLeft, ChevronRight, LogOut, Bell,
  Search, ChevronDown, Sun, Moon, Home, Upload, BarChart3, Package, Layers, Globe, User
} from '@nexdel/core/utils/icons';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem
} from '../components/dropdown-menu';
import { useTheme } from '@nexdel/core/hooks/use-theme';
import { useAuth, usePermissions } from '@nexdel/core/auth';
import { entityIcons } from '@nexdel/core/utils/entityIcons';
import { useUnreadCount } from '@nexdel/core/hooks/use-notifications';
import { useOrganization } from '@nexdel/core/hooks/use-organization';
import { useUpdateOwnProfile } from '@nexdel/core/hooks/use-users';
import { useTranslation } from 'react-i18next';
import { SearchModal } from './SearchModal';

const clientRecordItems = [
  { labelKey: 'sidebar.suppliers', icon: entityIcons.supplier, path: '/suppliers' },
  { labelKey: 'sidebar.contacts', icon: entityIcons.contact, path: '/contacts' },
  { labelKey: 'sidebar.mandates', icon: entityIcons.mandate, path: '/mandates' },
  { labelKey: 'sidebar.requirements', icon: entityIcons.requirement, path: '/requirements' },
  { labelKey: 'sidebar.submissions', icon: Layers, path: '/submissions' },
];

const supplierRecordItems = [
  { labelKey: 'sidebar.myMandates', icon: entityIcons.mandate, path: '/mandates' },
  { labelKey: 'sidebar.requirements', icon: entityIcons.requirement, path: '/requirements' },
  { labelKey: 'sidebar.submissions', icon: Layers, path: '/submissions' },
];

export function AppSidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const [recordsOpen, setRecordsOpen] = useState(true);
  const [analyticsOpen, setAnalyticsOpen] = useState(true);
  const [supplierAnalyticsOpen, setSupplierAnalyticsOpen] = useState(true);
  const location = useLocation();
  const { theme, toggle } = useTheme();
  const { signOut, profile } = useAuth();
  const isSupplier = profile?.userKind === 'supplier';
  const { canManageOrg, canViewAll } = usePermissions();
  // Work teams (no VIEW_ALL) don't see the supplier/contact sections — purchasing's tools.
  const recordItems = isSupplier
    ? supplierRecordItems
    : clientRecordItems.filter(it => canViewAll || (it.path !== '/suppliers' && it.path !== '/contacts'));
  const nav = useNavigate();
  const { t, i18n } = useTranslation();
  const { data: unreadData } = useUnreadCount();
  const unreadCount = unreadData?.count ?? 0;
  const { data: org } = useOrganization();
  const updateProfile = useUpdateOwnProfile();
  const orgInitials = org?.name ? org.name.slice(0, 2).toUpperCase() : '…';
  const [searchOpen, setSearchOpen] = useState(false);

  // Scroll-aware edge fades for the nav list. Since scrollbars are hidden globally, fade the items
  // into the rail to hint there's more to scroll — but only on the edge with hidden content.
  const navRef = useRef<HTMLDivElement>(null);
  const [fade, setFade] = useState({ top: false, bottom: false });
  const recomputeFade = useCallback(() => {
    const el = navRef.current;
    if (!el) return;
    const top = el.scrollTop > 1;
    const bottom = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
    setFade((prev) => (prev.top === top && prev.bottom === bottom ? prev : { top, bottom }));
  }, []);
  useEffect(() => {
    recomputeFade();
    window.addEventListener('resize', recomputeFade);
    return () => window.removeEventListener('resize', recomputeFade);
    // Re-check whenever section toggles change the content height.
  }, [recomputeFade, collapsed, recordsOpen, analyticsOpen, supplierAnalyticsOpen, recordItems.length]);

  const FADE = '24px';
  const navMask = fade.top && fade.bottom
    ? `linear-gradient(to bottom, transparent 0, #000 ${FADE}, #000 calc(100% - ${FADE}), transparent 100%)`
    : fade.top
    ? `linear-gradient(to bottom, transparent 0, #000 ${FADE})`
    : fade.bottom
    ? `linear-gradient(to bottom, #000 calc(100% - ${FADE}), transparent 100%)`
    : undefined;

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(prev => !prev);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const currentLang = i18n.language?.startsWith('fr') ? 'fr' : 'en';
  const toggleLanguage = () => {
    const next = currentLang === 'fr' ? 'en' : 'fr';
    i18n.changeLanguage(next);
    // Persist the choice server-side (used for email localization). Fire-and-forget — the UI has
    // already switched; a failed save just means the preference isn't remembered across devices.
    updateProfile.mutate({ language: next });
  };

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  // Light neutral rail: items are quiet (muted) by default; the brand green appears only on the
  // active item — a faint tint plus a left accent tick.
  const linkClass = (path: string) =>
    `flex items-center gap-3 px-3 py-[7px] rounded-md text-sm transition-colors ${
      isActive(path)
        ? 'text-primary bg-primary/10 font-medium shadow-[inset_2px_0_0_0_hsl(var(--primary))]'
        : 'text-muted-foreground hover:text-foreground hover:bg-accent'
    }`;

  const collapsedLinkClass = (path: string) =>
    `flex items-center justify-center py-[7px] rounded-md text-sm transition-colors ${
      isActive(path)
        ? 'text-primary bg-primary/10 font-medium shadow-[inset_2px_0_0_0_hsl(var(--primary))]'
        : 'text-muted-foreground hover:text-foreground hover:bg-accent'
    }`;

  const utilityClass = (extra = '') =>
    `flex items-center w-full py-[7px] rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors ${extra}`;

  const sectionLabel = 'text-xs uppercase tracking-wider text-muted-foreground/70 font-medium px-3 mb-1.5';
  const divider = 'border-t border-border my-2 mx-2';

  return (
    <aside
      className="flex flex-col h-screen sticky top-0 z-20 transition-all duration-200 app-sidebar"
      style={{
        width: collapsed ? 64 : 220,
        minWidth: collapsed ? 64 : 220,
      }}
    >
      {/* Logo + Workspace */}
      <div className="flex items-center h-11 px-3 border-b border-border">
        {!collapsed ? (
          <button className="flex items-center gap-2 px-1 py-1 rounded-md hover:bg-accent transition w-full">
            {org?.logoUrl ? (
              <img src={org.logoUrl} alt={org.name} className="w-7 h-7 rounded-lg object-cover" />
            ) : (
              <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center text-xs font-bold text-primary shrink-0">{orgInitials}</div>
            )}
            <div className="flex-1 text-left min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">{org?.name ?? '…'}</p>
            </div>
            <ChevronDown size={14} className="text-muted-foreground shrink-0" />
          </button>
        ) : (
          <div className="w-full flex justify-center">
            {org?.logoUrl ? (
              <img src={org.logoUrl} alt={org.name} className="w-8 h-8 rounded-lg object-cover" />
            ) : (
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">{orgInitials}</div>
            )}
          </div>
        )}
      </div>

      <div
        ref={navRef}
        onScroll={recomputeFade}
        className="flex-1 overflow-y-auto py-2"
        style={navMask ? { WebkitMaskImage: navMask, maskImage: navMask } : undefined}
      >
        {/* Quick Actions */}
        {!collapsed && (
          <div className="px-2 mb-1">
            <button
              onClick={() => setSearchOpen(true)}
              className="flex items-center gap-2.5 w-full px-3 py-[7px] rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition"
            >
              <Search size={15} />
              <span className="flex-1 text-left">{t('sidebar.search')}</span>
              <span className="text-xs text-muted-foreground bg-muted border border-border rounded px-1.5 py-0.5 font-mono">{t('sidebar.searchShortcut')}</span>
            </button>
          </div>
        )}
        {collapsed && (
          <div className="px-2 mb-1 flex justify-center">
            <button
              onClick={() => setSearchOpen(true)}
              className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition"
            >
              <Search size={16} />
            </button>
          </div>
        )}

        <div className={divider} />

        {/* Top-level items */}
        <div className="px-2 space-y-0.5">
          <NavLink to="/dashboard" className={collapsed ? collapsedLinkClass('/dashboard') : linkClass('/dashboard')}>
            <Home size={16} />
            {!collapsed && <span>{t('sidebar.home')}</span>}
          </NavLink>
          <NavLink to="/notifications" className={collapsed ? collapsedLinkClass('/notifications') : linkClass('/notifications')}>
            <Bell size={16} />
            {!collapsed && <span>{t('sidebar.notifications')}</span>}
            {!collapsed && unreadCount > 0 && (
              <span className="ml-auto text-xs bg-primary/15 text-primary rounded-full px-1.5 py-0.5 font-medium font-mono tabular-nums">{unreadCount}</span>
            )}
          </NavLink>
        </div>

        <div className={divider} />

        {/* Records */}
        <div className="px-2">
          {!collapsed ? (
            <>
              <button
                onClick={() => setRecordsOpen(!recordsOpen)}
                className="flex items-center w-full px-3 mb-1.5 group"
              >
                <span className={sectionLabel + ' mb-0 flex-1 text-left'}>{t('sidebar.records')}</span>
                <ChevronDown size={12} className={`text-muted-foreground transition-transform ${recordsOpen ? '' : '-rotate-90'}`} />
              </button>
              {recordsOpen && (
                <div className="space-y-0.5">
                {recordItems.map((item) => (
                    <NavLink key={item.path} to={item.path} className={linkClass(item.path)}>
                      <item.icon size={16} />
                      <span>{t(item.labelKey)}</span>
                    </NavLink>
                  ))}
                  {!isSupplier && canManageOrg && (
                    <NavLink to="/data" className={linkClass('/data')}>
                      <Upload size={16} />
                      <span>{t('sidebar.dataManagement')}</span>
                    </NavLink>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="space-y-0.5">
              {recordItems.map((item) => (
                <NavLink key={item.path} to={item.path} className={collapsedLinkClass(item.path)}>
                  <item.icon size={16} />
                </NavLink>
              ))}
              {!isSupplier && canManageOrg && (
                <NavLink to="/data" className={collapsedLinkClass('/data')}>
                  <Upload size={16} />
                </NavLink>
              )}
            </div>
          )}
        </div>

        {/* Analytics - purchase/admin only */}
        {!isSupplier && canViewAll && (
          <>
            <div className={divider} />
            <div className="px-2">
              {!collapsed ? (
                <>
                  <button
                    onClick={() => setAnalyticsOpen(!analyticsOpen)}
                    className="flex items-center w-full px-3 mb-1.5 group"
                  >
                    <span className={sectionLabel + ' mb-0 flex-1 text-left'}>{t('sidebar.analytics')}</span>
                    <ChevronDown size={12} className={`text-muted-foreground transition-transform ${analyticsOpen ? '' : '-rotate-90'}`} />
                  </button>
                  {analyticsOpen && (
                    <div className="space-y-0.5">
                      <button
                        onClick={() => setSupplierAnalyticsOpen(!supplierAnalyticsOpen)}
                        className="flex items-center gap-3 w-full px-3 py-[7px] rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                      >
                        <BarChart3 size={16} />
                        <span className="flex-1 text-left">{t('sidebar.supplierAnalytics')}</span>
                        <ChevronDown size={12} className={`text-muted-foreground transition-transform ${supplierAnalyticsOpen ? '' : '-rotate-90'}`} />
                      </button>
                      {supplierAnalyticsOpen && (
                        <div className="space-y-0.5 pl-4">
                          <NavLink to="/supplier-analytics" className={linkClass('/supplier-analytics')}>
                            <entityIcons.proposition size={14} />
                            <span>{t('sidebar.candidates')}</span>
                          </NavLink>
                          <NavLink to="/deliverable-analytics" className={linkClass('/deliverable-analytics')}>
                            <entityIcons.deliverableRequirement size={14} />
                            <span>{t('sidebar.deliverables')}</span>
                          </NavLink>
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="space-y-0.5">
                  <NavLink to="/supplier-analytics" className={collapsedLinkClass('/supplier-analytics')}>
                    <BarChart3 size={16} />
                  </NavLink>
                  <NavLink to="/deliverable-analytics" className={collapsedLinkClass('/deliverable-analytics')}>
                    <Package size={16} />
                  </NavLink>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Bottom */}
      <div className="px-2 pb-3 space-y-1 border-t border-border pt-3">
        {!isSupplier && canManageOrg && (
          <NavLink to="/settings" className={collapsed ? collapsedLinkClass('/settings') : linkClass('/settings')}>
            <Settings size={16} />
            {!collapsed && <span>{t('sidebar.settings')}</span>}
          </NavLink>
        )}

        {/* Theme toggle */}
        <button
          onClick={toggle}
          className={utilityClass(collapsed ? 'justify-center' : 'gap-3 px-3')}
        >
          {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
          {!collapsed && <span>{theme === 'light' ? t('sidebar.darkMode') : t('sidebar.lightMode')}</span>}
        </button>

        {/* Language toggle */}
        {!collapsed ? (
          <button onClick={toggleLanguage} className={utilityClass('gap-3 px-3')}>
            <Globe size={16} />
            <span className="flex-1 text-left">{currentLang === 'fr' ? t('sidebar.languageFrench') : t('sidebar.languageEnglish')}</span>
            <span className="text-xs font-mono text-muted-foreground">{currentLang === 'fr' ? 'EN' : 'FR'}</span>
          </button>
        ) : (
          <button onClick={toggleLanguage} className={utilityClass('justify-center font-mono text-xs')}>
            {currentLang.toUpperCase()}
          </button>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className={`flex items-center w-full py-2 rounded-md hover:bg-accent transition ${collapsed ? 'justify-center' : 'gap-3 px-3'}`}>
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium bg-muted text-foreground shrink-0">
                {profile?.fullName?.slice(0, 2).toUpperCase() ?? '??'}
              </div>
              {!collapsed && (
                <>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-xs font-medium truncate text-foreground">{profile?.fullName ?? profile?.email ?? '…'}</p>
                  </div>
                  <ChevronDown size={14} className="text-muted-foreground shrink-0" />
                </>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side="top" className="min-w-[180px] text-xs">
            <DropdownMenuItem className="gap-2 text-xs" onSelect={() => nav('/profile')}>
              <User size={13} />
              {t('sidebar.myProfile')}
            </DropdownMenuItem>
            <DropdownMenuItem className="gap-2 text-xs" onSelect={() => { signOut(); nav('/login'); }}>
              <LogOut size={13} />
              {t('sidebar.signOut')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <button
          onClick={() => setCollapsed(!collapsed)}
          className="flex items-center justify-center w-full py-1.5 rounded-md hover:bg-accent hover:text-foreground transition text-muted-foreground"
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>
      <SearchModal open={searchOpen} onOpenChange={setSearchOpen} />
    </aside>
  );
}
